import { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { useStateContext } from '../../../../../contexts/ContextProvider';

export const useClienti = () => {
  const { message } = App.useApp();
  const { token } = useStateContext();
  const [clienti, setClienti] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });


  const fetchAmo = useCallback(async (leadId) => {
    if (!token) return null;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/get_amo_lead?token=${token}&lead_id=${leadId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Date AMO:', data);
        message.success('Datele au fost importate cu succes din AMO');
        return data;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la importul datelor din AMO');
        return null;
      }
    } catch (error) {
      console.error('Eroare la fetch AMO:', error);
      message.error(error.message || 'Eroare de conexiune');
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchClienti = useCallback(
    async ({
      page = 1,
      pageSize = 10,
      search = '',
      sortBy = '',
      sortOrder = 'asc'
    } = {}) => {
      if (!token) return;

      const params = new URLSearchParams();
      params.append('token', token);
      params.append('page', page);
      params.append('page_size', pageSize);
      if (search) params.append('search', search);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);

      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/clienti?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setClienti(data.items || []);
          setPagination(prev => ({
            ...prev,
            current: data.page || page,
            total: data.total || 0,
            pageSize: data.page_size || pageSize
          }));
        } else {
          const errorData = await response.json();
          message.error(errorData.detail || 'Eroare la încărcarea clienților');
        }
      } catch (error) {
        console.error('Eroare la fetch clienți:', error);
        message.error(error.message || 'Eroare de conexiune');
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const fetchClient = useCallback(async (clientId) => {
    if (!token) return null;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/clienti/${clientId}?token=${token}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setClient(data);
        return data;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la încărcarea clientului');
        return null;
      }
    } catch (error) {
      console.error('Eroare la fetch client:', error);
      message.error(error.message || 'Eroare de conexiune');
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createClient = useCallback(async (clientData) => {
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/clienti?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(clientData)
      });

      if (response.ok) {
        message.success('Clientul a fost creat cu succes');
        fetchClienti({
          page: pagination.current,
          pageSize: pagination.pageSize
        });
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la crearea clientului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la crearea clientului:', error);
      message.error(error.message || 'Eroare de conexiune');
      return false;
    }
  }, [token, fetchClienti, pagination]);

  const updateClient = useCallback(async (id, clientData) => {
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/clienti/${id}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(clientData)
      });

      if (response.ok) {
        message.success('Clientul a fost actualizat cu succes');
        fetchClienti({
          page: pagination.current,
          pageSize: pagination.pageSize
        });
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la actualizarea clientului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea clientului:', error);
      message.error(error.message || 'Eroare de conexiune');
      return false;
    }
  }, [token, fetchClienti, pagination]);

  const deleteClient = useCallback(async (id) => {
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/clienti/${id}?token=${token}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        message.success('Clientul a fost șters cu succes');
        fetchClienti({
          page: pagination.current,
          pageSize: pagination.pageSize
        });
        return true;
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || 'Eroare la ștergerea clientului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la ștergerea clientului:', error);
      message.error(error.message || 'Eroare de conexiune');
      return false;
    }
  }, [token, fetchClienti, pagination]);

  return {
    clienti,
    client,
    loading,
    pagination,
    fetchClienti,
    fetchClient,
    createClient,
    updateClient,
    deleteClient,
    fetchAmo
  };
}; 